/**
 * useOrderSelection
 *
 * Selection state management hook for order list with shift-click range selection.
 * Handles selection state reset when orders array changes (due to filtering).
 *
 * Features:
 * - Single click toggle selection
 * - Shift+click for range selection
 * - Select all / deselect all
 * - Automatic cleanup of stale selections when orders change
 * - Resets lastSelectedIndex when orders array changes
 * - BLU-718: honours `unselectableIds` so rows the backend will reject
 *   (orders still inside the customer-edit hold window) can never enter the
 *   selection — not via select-all, not via shift-range, not via a click.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { QueuedOrder } from '../types';

interface UseOrderSelectionOptions {
  orders: QueuedOrder[];
  /**
   * Ids that must never be selected. createBatch rejects a batch ALL-OR-NOTHING
   * when any order is still held (production-orders.dao.ts), so one unselectable
   * row silently sweeping in via select-all fails the entire batch.
   */
  unselectableIds?: ReadonlySet<string>;
}

interface UseOrderSelectionReturn {
  selectedIds: Set<string>;
  toggleSelect: (orderId: string, event?: React.MouseEvent | React.KeyboardEvent) => void;
  selectAll: () => void;
  clearSelection: () => void;
  isAllSelected: boolean;
  isSomeSelected: boolean;
  selectedCount: number;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

export function useOrderSelection({
  orders,
  unselectableIds = EMPTY_IDS,
}: UseOrderSelectionOptions): UseOrderSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastSelectedIndexRef = useRef<number | null>(null);
  const previousOrdersRef = useRef<QueuedOrder[]>(orders);

  // Read the predicate through a ref so the callbacks below keep a stable
  // identity. `unselectableIds` is recomputed on every hold-countdown tick
  // (~30s); putting it in the dep arrays would re-render every memoised row.
  const unselectableRef = useRef(unselectableIds);
  unselectableRef.current = unselectableIds;

  const selectableCount = orders.reduce(
    (n, o) => (unselectableIds.has(o.id) ? n : n + 1),
    0
  );

  // CRITICAL: Reset lastSelectedIndex when orders array changes (filters applied)
  // This prevents stale index references causing incorrect range selection
  useEffect(() => {
    if (previousOrdersRef.current !== orders) {
      lastSelectedIndexRef.current = null;
      previousOrdersRef.current = orders;

      // Also clear selection if selected orders no longer exist
      setSelectedIds(prev => {
        const orderIds = new Set(orders.map(o => o.id));
        const validIds = new Set(Array.from(prev).filter(id => orderIds.has(id)));
        if (validIds.size !== prev.size) {
          return validIds;
        }
        return prev;
      });
    }
  }, [orders]);

  // BLU-718: prune anything that BECAME unselectable while selected. A hold is
  // normally stamped before the operator ever sees the row, but a re-sync can
  // re-hold an already-selected order — dropping it here keeps the selection in
  // step with what createBatch will actually accept.
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const kept = new Set(Array.from(prev).filter(id => !unselectableIds.has(id)));
      return kept.size === prev.size ? prev : kept;
    });
  }, [unselectableIds]);

  const toggleSelect = useCallback((orderId: string, event?: React.MouseEvent | React.KeyboardEvent) => {
    // BLU-718: a held row is never selectable, and never becomes a range anchor.
    if (unselectableRef.current.has(orderId)) return;
    const currentIndex = orders.findIndex(o => o.id === orderId);
    if (currentIndex === -1) return;

    // Check for shift key (mouse or keyboard)
    const isShiftKey = event && 'shiftKey' in event && event.shiftKey;

    if (isShiftKey && lastSelectedIndexRef.current !== null) {
      // Shift-click: select range (skipping held rows inside the range)
      const start = Math.min(lastSelectedIndexRef.current, currentIndex);
      const end = Math.max(lastSelectedIndexRef.current, currentIndex);
      const rangeIds = orders
        .slice(start, end + 1)
        .map(o => o.id)
        .filter(id => !unselectableRef.current.has(id));

      setSelectedIds(prev => {
        const next = new Set(prev);
        rangeIds.forEach(id => next.add(id));
        return next;
      });
    } else {
      // Normal click: toggle single
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(orderId)) {
          next.delete(orderId);
        } else {
          next.add(orderId);
        }
        return next;
      });
    }

    lastSelectedIndexRef.current = currentIndex;
  }, [orders]);

  const selectAll = useCallback(() => {
    // BLU-718: "all" means all SELECTABLE rows. Held orders are excluded, so
    // select-all can no longer poison the batch with a row createBatch rejects.
    const selectable = orders.filter(o => !unselectableRef.current.has(o.id));
    if (selectable.length > 0 && selectedIds.size === selectable.length) {
      setSelectedIds(new Set());
      lastSelectedIndexRef.current = null;
    } else {
      setSelectedIds(new Set(selectable.map(o => o.id)));
      lastSelectedIndexRef.current = orders.length - 1;
    }
  }, [orders, selectedIds.size]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedIndexRef.current = null;
  }, []);

  // BLU-718: "all selected" is measured against the SELECTABLE rows, so the
  // header checkbox still reads as fully checked when the only unchecked rows
  // are held ones the operator cannot select anyway.
  const isAllSelected = selectableCount > 0 && selectedIds.size === selectableCount;
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  return {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    isAllSelected,
    isSomeSelected,
    selectedCount: selectedIds.size,
  };
}
